import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Copy, ExternalLink, Loader2, FlaskConical } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (cents) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);

const STATUS_LABEL = {
  pending: { label: "En attente", cls: "border-amber-400/40 bg-amber-500/10 text-amber-300" },
  paid: { label: "Payé", cls: "border-emerald-400/40 bg-emerald-500/10 text-emerald-300" },
  expired: { label: "Expiré", cls: "border-ink-line bg-ink-soft text-mist" },
};

const fieldCls =
  "w-full rounded-xl border border-ink-line bg-ink-soft/80 px-4 py-3 text-sm text-white placeholder:text-mist/50 outline-none transition-all duration-200 focus:border-viol/60 focus:ring-2 focus:ring-viol/25";

export const PaymentsPanel = () => {
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [clientName, setClientName] = useState("");
  const [creating, setCreating] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("gt_admin_token");
      const { data } = await axios.get(`${API}/admin/payments`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setPayments(data);
    } catch (e) {
      toast.error("Impossible de charger les acomptes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const value = parseFloat(amount.replace(",", "."));
    if (!value || value < 1) {
      toast.error("Montant invalide (minimum 1 €).");
      return;
    }
    if (!reference.trim()) {
      toast.error("Ajoutez une référence client (ex. nom du client + numéro de devis).");
      return;
    }
    setCreating(true);
    try {
      const token = sessionStorage.getItem("gt_admin_token");
      const { data } = await axios.post(
        `${API}/admin/payment-links`,
        { amount_eur: value, reference: reference.trim(), client_name: clientName.trim() || null },
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true }
      );
      setLastLink(data);
      setAmount("");
      setReference("");
      setClientName("");
      setCopied(false);
      toast.success("Lien d'acompte créé. Envoyez-le uniquement au client concerné.");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Création du lien impossible pour le moment.");
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    if (!lastLink) return;
    try {
      await navigator.clipboard.writeText(lastLink.checkout_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copie impossible — sélectionnez le lien manuellement.");
    }
  };

  return (
    <div className="space-y-6" data-testid="payments-panel">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 font-mono text-[11px] text-amber-300" data-testid="stripe-test-badge">
          <FlaskConical size={12} />
          Stripe mode TEST — aucun paiement réel
        </span>
      </div>

      <form onSubmit={create} className="rounded-2xl border border-ink-line bg-ink-surface/60 p-6 space-y-4">
        <div>
          <h3 className="font-display font-bold text-lg text-white">Nouvel acompte</h3>
          <p className="mt-1 text-xs text-mist">
            Créez le lien après validation du devis. Montant et référence ne sont jamais affichés sur le site public.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label htmlFor="pay-amount" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
              Montant (€) *
            </label>
            <input
              id="pay-amount"
              data-testid="payment-amount-input"
              inputMode="decimal"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="1500"
              className={fieldCls}
            />
          </div>
          <div>
            <label htmlFor="pay-ref" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
              Référence (devis) *
            </label>
            <input
              id="pay-ref"
              data-testid="payment-reference-input"
              required
              maxLength={120}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Devis 2026-014 — Martin"
              className={fieldCls}
            />
          </div>
          <div>
            <label htmlFor="pay-client" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
              Nom du client (optionnel)
            </label>
            <input
              id="pay-client"
              data-testid="payment-client-input"
              maxLength={120}
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Boulangerie Martin"
              className={fieldCls}
            />
          </div>
        </div>
        <button
          data-testid="payment-create-btn"
          type="submit"
          disabled={creating}
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-iris-deep to-viol px-6 py-3 text-sm font-medium text-white transition-all duration-300 hover:shadow-[0_0_40px_rgba(139,92,246,0.5)] disabled:opacity-60"
        >
          {creating && <Loader2 size={16} className="animate-spin" />}
          Créer le lien d'acompte
        </button>

        {lastLink && (
          <div className="rounded-xl border border-viol/30 bg-viol/5 p-4" data-testid="payment-link-output">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-viol-pale mb-2">
              Lien prêt — {eur(lastLink.amount)}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                readOnly
                value={lastLink.checkout_url}
                onFocus={(e) => e.target.select()}
                className="h-10 flex-1 rounded-lg border border-ink-line bg-ink-soft/80 px-3 font-mono text-xs text-mist"
              />
              <button
                type="button"
                data-testid="payment-copy-btn"
                onClick={copy}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-ink-line px-4 py-2 text-xs text-mist hover:text-white hover:border-viol/50 transition-all"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copié" : "Copier"}
              </button>
              <a
                href={lastLink.checkout_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="payment-open-btn"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-ink-line px-4 py-2 text-xs text-mist hover:text-white hover:border-viol/50 transition-all"
              >
                <ExternalLink size={14} />
                Ouvrir
              </a>
            </div>
          </div>
        )}
      </form>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-white">Acomptes envoyés</h3>
          <button
            data-testid="payments-refresh-btn"
            onClick={load}
            className="text-xs text-mist hover:text-white underline underline-offset-4"
          >
            Rafraîchir
          </button>
        </div>
        {loading && payments === null ? (
          <div className="flex justify-center py-10 text-mist">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : payments && payments.length === 0 ? (
          <p className="py-8 text-center text-sm text-mist" data-testid="payments-empty">
            Aucun acompte pour l'instant.
          </p>
        ) : (
          <div className="space-y-3" data-testid="payments-list">
            {payments?.map((p) => {
              const st = STATUS_LABEL[p.payment_status] || STATUS_LABEL.pending;
              return (
                <article
                  key={p.id}
                  data-testid="payment-item"
                  className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink-line bg-ink-surface/60 p-5"
                >
                  <div>
                    <p className="font-medium text-white text-sm">{p.reference}</p>
                    {p.client_name && <p className="text-xs text-mist mt-0.5">{p.client_name}</p>}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-display font-bold text-white">{eur(p.amount)}</span>
                    <span className={`inline-flex items-center rounded-full border px-3 py-1 font-mono text-[11px] ${st.cls}`}>
                      {st.label}
                    </span>
                    <span className="font-mono text-[11px] text-mist/60 hidden sm:block">
                      {new Date(p.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsPanel;
